import clsx from 'clsx'
import { useRef } from 'react'
import { useLegacyPosition, useManageOpen } from 'shared/position'
import { contextActions, typo } from 'shared/styles'

import type { Conversation } from '../../model/types'

import { api } from '../../api'
import { useFilter, useShowTabs } from '../../hooks/useFilters'
import { updateRead, updateStar } from '../../model/stores'
import { $confirmStore, ConfirmDialogMode } from '../ConfirmDialog'
import { useConversation } from './hooks/useConversation'
import css from './QuickActions.module.scss'
import { store } from './utils'

const actionCN = clsx(contextActions.action, css.action)

export function QuickActions() {
	const ref = useRef<HTMLDivElement | null>(null)

	const conversation = useConversation()
	useManageOpen(ref, store)
	useLegacyPosition(ref, store)

	return (
		<div
			className={clsx(css.menu, contextActions.menu, typo.buttonSmB)}
			// @ts-ignore Recheck in React 19
			popover='auto'
			ref={ref}
		>
			{conversation && (
				<>
					<OtherOrFocused {...conversation} />
					<Read {...conversation} />
					<Star {...conversation} />
					<Archive {...conversation} />
					<DeleteAction {...conversation} />
				</>
			)}
		</div>
	)
}

function Archive({ entity_id: id }: Conversation) {
	const filter = useFilter()

	const inOnArchive = filter === 'archive'
	const [mutation, { isLoading, originalArgs }] = api.useArchiveMutation()

	return (
		<button
			className={actionCN}
			disabled={isLoading && originalArgs?.id === id}
			onClick={() => {
				mutation({
					id,
					mark: !inOnArchive
				})
				store.close()
			}}
			type='button'
		>
			{inOnArchive ? 'Restore' : 'Archive'}
		</button>
	)
}

function DeleteAction({ entity_id: conversation }: Conversation) {
	const [remove, { isLoading, originalArgs }] = api.useDeleteMutation()
	const isInProcess = isLoading && originalArgs === conversation

	return (
		<button
			className={actionCN}
			disabled={isInProcess}
			onClick={() => {
				$confirmStore.set({
					confirm: () => remove(conversation),
					mode: ConfirmDialogMode.DeleteConversation
				})

				store.close()
			}}
			type='button'
		>
			Delete conversation
		</button>
	)
}

function OtherOrFocused({ entity_id: conversation }: Conversation) {
	const filter = useFilter(),
		shouldShow = useShowTabs()

	const [focusedMutation, focusedOptions] = api.useSetFocusMutation(),
		[otherMutation, otherOptions] = api.useSetOtherMutation()

	if (!shouldShow) {
		return null
	}

	const isOnFocused = filter === 'focused'
	const isOnGoing = focusedOptions.isLoading || otherOptions.isLoading

	const mutation = isOnFocused ? otherMutation : focusedMutation
	const label = `Move to ${isOnFocused ? 'Other' : 'Focused'}`

	return (
		<button
			className={actionCN}
			disabled={
				isOnGoing &&
				(focusedOptions.originalArgs === conversation ||
					otherOptions.originalArgs === conversation)
			}
			onClick={() => {
				mutation(conversation)
				store.close()
			}}
			type='button'
		>
			{label}
		</button>
	)
}

function Read({ entity_id: id, unread_count: unread }: Conversation) {
	const [readMutation, { isLoading, originalArgs }] = api.useMarkReadMutation()

	const isUnread = Boolean(unread)

	const label = `Mark as ${isUnread ? 'read' : 'unread'}`

	return (
		<button
			className={actionCN}
			disabled={isLoading && originalArgs?.id === id}
			onClick={async () => {
				const oldBody = {
					id,
					mark: !isUnread
				}

				const newBody = {
					id,
					mark: isUnread
				}

				updateRead(newBody)
				store.close()
				try {
					await readMutation(newBody).unwrap()
				} catch (e) {
					updateRead(oldBody)
				}
			}}
			type='button'
		>
			{label}
		</button>
	)
}

function Star({ entity_id: id, is_starred: isStarred }: Conversation) {
	const [starMutation, { isLoading, originalArgs }] = api.useStarMutation()

	return (
		<button
			className={actionCN}
			disabled={isLoading && originalArgs?.id === id}
			onClick={async () => {
				const oldBody = {
					id,
					mark: isStarred
				}

				const newBody = {
					id,
					mark: !isStarred
				}

				updateStar(newBody)
				store.close()
				try {
					await starMutation(newBody).unwrap()
				} catch (e) {
					updateStar(oldBody)
				}
			}}
			type='button'
		>
			{isStarred ? 'Remove star' : 'Star'}
		</button>
	)
}
